/**
 * High-speed feel: maps horizontal speed to a smoothed 0..1 "rush" level that
 * drives a camera FOV kick (every quality preset) and a deeper look-pass
 * vignette (medium+ where the composer runs). Pure math — the caller gates it
 * behind `prefers-reduced-motion` and feeds zero speed while paused/noclip.
 */

/** Speed (u/s) where the rush starts to ease in. */
const RUSH_START_SPEED = 900;
/** Speed (u/s) where the rush is fully engaged. */
const RUSH_FULL_SPEED = 2200;
/** Smoothing time constant (seconds) — how fast the level chases its target. */
const RUSH_SMOOTHING_TAU = 0.25;
/** Max FOV kick in degrees at full rush. */
const MAX_FOV_KICK_DEG = 10;
/** Max extra look-pass vignette at full rush (base vignette is 0.28). */
const MAX_VIGNETTE_BOOST = 0.22;

export class SpeedFeel {
  private level = 0;

  /**
   * Advance the smoothed rush level toward this frame's target. Exponential
   * approach, so the result is frame-rate independent. Returns the new level.
   */
  update(horizontalSpeed: number, dt: number): number {
    const t = (horizontalSpeed - RUSH_START_SPEED) / (RUSH_FULL_SPEED - RUSH_START_SPEED);
    const target = Math.min(1, Math.max(0, t));
    const blend = 1 - Math.exp(-dt / RUSH_SMOOTHING_TAU);
    this.level += (target - this.level) * blend;
    return this.level;
  }

  /** Degrees to add to the base FOV this frame. */
  fovKick(): number {
    return this.level * MAX_FOV_KICK_DEG;
  }

  /** Extra vignette weight for the look pass this frame. */
  vignetteBoost(): number {
    return this.level * MAX_VIGNETTE_BOOST;
  }

  reset(): void {
    this.level = 0;
  }
}
